import { useEffect, useState, type ChangeEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { AppLayout } from '../components/AppLayout'
import { kycApi, meApi, type KycStatusResponse } from '../api/endpoints'
import { watchAuthState } from '../lib/firebase'
import { COUNTRIES } from '../lib/countries'

/**
 * /kyc — buyer KYC submission. Status pill at the top, "why we ask" copy,
 * a tile grid for the document types, list of submitted docs at the bottom.
 * KYC-approval is what unlocks on-site (storefront) checkout, so a buyer who
 * hits the checkout gate is bounced here.
 *
 * Backed by /api/me/kyc/status + the 3-step signed-URL upload (upload-url →
 * PUT → submit) in kycApi.upload. Doc types match the backend enum exactly.
 */

const DOC_TYPES: Array<{ key: string; label: string; hint: string }> = [
  { key: 'id', label: 'Government-issued ID', hint: 'National ID or driver’s license — front (and back if applicable).' },
  { key: 'passport', label: 'Passport', hint: 'Photo page, clearly visible.' },
  { key: 'address_proof', label: 'Proof of address', hint: 'Utility bill or bank statement, < 90 days.' },
  { key: 'business_license', label: 'Business license', hint: 'For business / trade buyers only.' },
  { key: 'other', label: 'Other supporting doc', hint: 'Tax ID, company registration, or anything else relevant.' },
]

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic'
const MAX_BYTES = 10 * 1024 * 1024

type StatusKind = 'approved' | 'submitted' | 'not_submitted'

const PILL_CLASS: Record<StatusKind, string> = {
  approved: 'bg-success/15 text-success',
  submitted: 'bg-warn/20 text-warn',
  not_submitted: 'bg-danger/15 text-danger',
}

const PILL_TEXT: Record<StatusKind, string> = {
  approved: 'Approved',
  submitted: 'Under review',
  not_submitted: 'Not submitted',
}

export function Kyc() {
  const [params] = useSearchParams()
  const fromCheckout = params.get('reason') === 'kyc_required'
  const [status, setStatus] = useState<KycStatusResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [endpointMissing, setEndpointMissing] = useState(false)
  const [uploadingType, setUploadingType] = useState<string | null>(null)
  const [uploadFlash, setUploadFlash] = useState<{ kind: 'success' | 'danger'; text: string } | null>(null)

  async function refreshStatus() {
    try {
      const s = await kycApi.status()
      setStatus(s)
      setEndpointMissing(false)
      setError(null)
    } catch (err: any) {
      if (err?.status === 404) setEndpointMissing(true)
      else setError(err?.message || 'Could not load KYC status')
    }
  }

  // Wait for Firebase auth state before calling the API. A hard refresh
  // on /kyc otherwise hits the endpoint before currentUser is hydrated,
  // yielding a 401 in the page. watchAuthState keeps the page reactive
  // when the user signs in/out from another tab.
  useEffect(() => {
    const unsub = watchAuthState((user) => {
      if (user) {
        refreshStatus()
      } else {
        setError('Sign in to manage your KYC documents.')
      }
    })
    return () => unsub()
  }, [])

  async function onFile(docType: string, e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > MAX_BYTES) {
      setUploadFlash({ kind: 'danger', text: `${file.name} is over 10 MB — please compress or scan at lower DPI.` })
      return
    }
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
    if (!['.pdf', '.jpg', '.jpeg', '.png', '.heic'].includes(ext)) {
      setUploadFlash({ kind: 'danger', text: 'Only PDF, JPG, PNG, HEIC files are accepted.' })
      return
    }
    setUploadFlash(null)
    setUploadingType(docType)
    try {
      await kycApi.upload(docType, file)
      setUploadFlash({ kind: 'success', text: 'Uploaded — our team will review and email you.' })
      await refreshStatus()
      setTimeout(() => setUploadFlash(null), 5000)
    } catch (err: any) {
      if (err?.status === 404) setEndpointMissing(true)
      else setUploadFlash({ kind: 'danger', text: err?.message || 'Upload failed — please try again.' })
    } finally {
      setUploadingType(null)
    }
  }

  return (
    <AppLayout>
      <div className="mx-auto max-w-3xl px-6 py-8 space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-ink tracking-tight">KYC verification</h1>
          <p className="mt-1 text-sm text-muted">
            Upload identity documents so we can verify and approve your account.
          </p>
        </div>

        {fromCheckout && (status?.status !== 'approved') && (
          <div className="rounded-card border border-warn/30 bg-warn/10 text-warn text-sm p-4" data-testid="kyc-from-checkout">
            Your account needs to be verified before you can check out on-site. Upload a document below —
            most accounts are approved within 1–2 business days, and we’ll email you when you’re cleared.
          </div>
        )}

        {error && (
          <div className="rounded-card border border-danger/30 bg-danger/5 text-danger text-sm p-4">{error}</div>
        )}

        {endpointMissing ? (
          <EmailFallback />
        ) : status ? (
          <>
            <StatusHeader status={status} />
            <CountryCard initial={status.country ?? null} />
            <WhyWeAsk />
            <UploadTiles
              status={status}
              uploadingType={uploadingType}
              uploadFlash={uploadFlash}
              onFile={onFile}
            />
            {status.documents.length > 0 && <SubmittedList docs={status.documents} />}
            <NeedHelp />
          </>
        ) : error ? (
          // Signed-out / errored: the banner above already explains it —
          // don't also render an empty loading card underneath it.
          null
        ) : (
          <div className="h-32 rounded-md skeleton" aria-hidden />
        )}
      </div>
    </AppLayout>
  )
}

function StatusHeader({ status }: { status: KycStatusResponse }) {
  const kind = (status.status || 'not_submitted') as StatusKind
  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="card"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-ink">Verification status</h3>
          <p className="mt-1 text-sm text-muted">{status.message || (
            kind === 'approved' ? 'Your account is verified — you can place orders.'
              : kind === 'submitted' ? 'Documents are under review. Most decisions land in 1–2 business days.'
                : 'Upload at least one ID document to start the verification process.'
          )}</p>
        </div>
        <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${PILL_CLASS[kind]}`}>
          {PILL_TEXT[kind]}
        </span>
      </div>
    </motion.section>
  )
}

/**
 * Business-registration country selector. Feeds the payment-rails matrix —
 * which payment methods (card / bank transfer) the buyer's checkout renders.
 * Saves on change; a webhook self-heal also stamps it from the first
 * completed checkout, so this is the explicit override.
 */
function CountryCard({ initial }: { initial: string | null }) {
  const [value, setValue] = useState(initial ?? '')
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')

  async function onChange(e: ChangeEvent<HTMLSelectElement>) {
    const code = e.target.value
    setValue(code)
    if (!code) return
    setState('saving')
    try {
      await meApi.setCountry(code)
      setState('saved')
      setTimeout(() => setState('idle'), 2500)
    } catch {
      setState('error')
    }
  }

  return (
    <section className="card" data-testid="kyc-country-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-ink">Where is your business registered?</h3>
          <p className="mt-1 text-sm text-muted">
            This decides which payment methods your checkout offers (card / bank transfer).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={value}
            onChange={(e) => void onChange(e)}
            disabled={state === 'saving'}
            className="h-10 rounded-md border border-border bg-paper px-3 text-sm text-ink disabled:opacity-50"
            data-testid="kyc-country-select"
            aria-label="Business registration country"
          >
            <option value="">Select country…</option>
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
          {state === 'saved' && <span className="text-xs font-semibold text-success">Saved</span>}
          {state === 'error' && <span className="text-xs font-semibold text-danger">Couldn’t save — retry</span>}
        </div>
      </div>
    </section>
  )
}

function WhyWeAsk() {
  return (
    <section className="card">
      <h3 className="text-base font-semibold text-ink">Why we ask for these documents</h3>
      <p className="mt-2 text-sm text-ink leading-relaxed">
        DiaEx compliance policy requires every buyer to verify their identity before placing orders.
        Documents are stored securely and reviewed by our team within <strong>1–2 business days</strong>.
        You'll receive an email when your status changes.
      </p>
      <ul className="mt-3 text-sm text-ink space-y-1 list-disc pl-5">
        <li>Government-issued photo ID (passport or driver's license)</li>
        <li>Proof of address (utility bill or bank statement, &lt; 90 days old)</li>
        <li>For business buyers: company registration + tax ID</li>
      </ul>
    </section>
  )
}

function UploadTiles({ status, uploadingType, uploadFlash, onFile }: {
  status: KycStatusResponse
  uploadingType: string | null
  uploadFlash: { kind: 'success' | 'danger'; text: string } | null
  onFile: (docType: string, e: ChangeEvent<HTMLInputElement>) => void | Promise<void>
}) {
  const uploaded = new Set((status.documents || []).map((d) => d.docType))
  return (
    <section className="card">
      <div>
        <h3 className="text-base font-semibold text-ink">Upload documents</h3>
        <p className="mt-1 text-xs text-muted">
          Click a tile to choose a file (PDF, JPG, PNG, HEIC; max 10 MB each).
        </p>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="kyc-doc-grid">
        {DOC_TYPES.map((dt) => {
          const isUploaded = uploaded.has(dt.key)
          const isBusy = uploadingType === dt.key
          return (
            <label
              key={dt.key}
              data-doc-type={dt.key}
              data-testid={`kyc-tile-${dt.key}`}
              className={`relative block cursor-pointer rounded-2xl p-4 transition-all ${
                isUploaded
                  ? 'border-2 border-success/60 bg-success/5'
                  : 'border-2 border-dashed border-line bg-paper hover:border-primary hover:bg-primary/5'
              }`}
            >
              <p className="text-sm font-semibold text-ink">{dt.label}</p>
              <p className="mt-1 text-[11px] text-muted">{dt.hint}</p>
              {isBusy && (
                <p className="mt-2 text-[11px] font-semibold text-primary">Uploading…</p>
              )}
              {isUploaded && (
                <span
                  className="absolute top-3 right-3 inline-flex h-6 w-6 items-center justify-center rounded-full bg-success text-white text-[12px] font-bold"
                  aria-label="uploaded"
                >
                  ✓
                </span>
              )}
              <input
                type="file"
                accept={ACCEPT}
                disabled={isBusy}
                className="hidden"
                onChange={(e) => onFile(dt.key, e)}
              />
            </label>
          )
        })}
      </div>

      {uploadFlash && (
        <div className={`mt-4 rounded-md text-sm px-3 py-2 ${
          uploadFlash.kind === 'success'
            ? 'bg-success/10 border border-success/30 text-success'
            : 'bg-danger/10 border border-danger/30 text-danger'
        }`}>
          {uploadFlash.text}
        </div>
      )}
    </section>
  )
}

const labelForDocType: Record<string, string> = Object.fromEntries(
  DOC_TYPES.map((d) => [d.key, d.label]),
)

function SubmittedList({ docs }: { docs: KycStatusResponse['documents'] }) {
  return (
    <section className="card">
      <h3 className="text-base font-semibold text-ink">Submitted documents</h3>
      <ul className="mt-3 divide-y divide-border">
        {docs.map((doc, i) => (
          <li key={`${doc.docType}-${i}`} className="flex items-center gap-3 py-2 text-sm">
            <span className="text-ink font-medium flex-1">
              {labelForDocType[doc.docType] || doc.docType}
            </span>
            <span className="text-xs text-soft">
              {Math.round((doc.sizeBytes || 0) / 1024)} KB · {new Date(doc.uploadedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

function NeedHelp() {
  return (
    <section className="card">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-soft">Need help?</h3>
      <p className="mt-2 text-xs text-muted leading-relaxed">
        Documents not loading or you'd rather email them? Send to{' '}
        <a className="text-primary underline" href="mailto:info@thediaex.com?subject=KYC%20documents">
          info@thediaex.com
        </a>
        {' '}with subject "KYC documents". Approval typically takes 1–2 business days.
      </p>
    </section>
  )
}

function EmailFallback() {
  return (
    <section className="card space-y-3">
      <p className="text-base font-semibold text-ink">KYC submission opening soon</p>
      <p className="text-sm text-muted leading-relaxed">
        We're finishing the in-app upload flow. In the meantime, please email
        your documents to{' '}
        <a className="text-primary underline" href="mailto:info@thediaex.com?subject=KYC%20documents">
          info@thediaex.com
        </a>
        {' '}with subject "KYC documents":
      </p>
      <ul className="text-sm text-muted leading-relaxed list-disc pl-5">
        <li>Government-issued photo ID</li>
        <li>Proof of address (utility bill / bank statement, within 90 days)</li>
        <li>For business accounts: company registration + tax ID</li>
      </ul>
    </section>
  )
}
